/**
 * Glyphs for downstream unit operations.
 *
 * A harvest operation template carries a glyph key in its `icon` column, and
 * that key resolves here. The registry is code and the database stores only
 * references, the same split `capabilities.ts` uses — so a key the registry
 * does not define is a key nothing can render, and the `DspGlyph` type makes
 * that a compile error rather than a blank cell.
 *
 * Two levels of meaning. The glyph is the exact operation; the family is the
 * kind of thing it is. Both hang off the template, so an operation looks
 * identical everywhere it appears — planner, journal, flow graph. A colour
 * keyed to anything else (a stage index, a position) remaps the moment the plan
 * changes and can never be learned.
 *
 * Drawn in the process-flow idiom rather than the lab idiom: equipment and
 * streams, no glassware. Paths live on a 16x16 grid with a 1-unit safe margin,
 * stroke-only — the renderer supplies stroke width and colour, so a path must
 * not set its own (`stroke-dasharray` excepted, which is part of the drawing).
 *
 * The set is iterated in a standalone studio; its export replaces the FAMILIES
 * and GLYPHS blocks below wholesale. Keep the keys stable when doing so, since
 * they are what the database holds.
 *
 * Cleaning is deliberately absent. It was a family of one and the drawing said
 * nothing a process engineer would group with anything else, so it was dropped
 * rather than kept for symmetry. Cleaning-in-place templates therefore render
 * the neutral mark until the vocabulary gains something for them.
 */
export const GLYPH_FAMILIES = {
    handling: { label: "Handling", color: "#6b7169" },
    thermal: { label: "Thermal", color: "#9a5f2b" },
    biochem: { label: "Biochemical", color: "#3f6b39" },
    separation: { label: "Separation", color: "#3f6280" },
    drying: { label: "Drying & solids", color: "#8a6a00" },
};
export const DSP_GLYPHS = {
    "receive": { label: "Receiving Broth", family: "handling", path: `<path d="M8 1.5v4.5"/><path d="M5.6 4.1L8 6.5l2.4-2.4"/>
     <path d="M2.6 8.4h10.8v3.9a1.7 1.7 0 0 1-1.7 1.7H4.3a1.7 1.7 0 0 1-1.7-1.7z"/>` },
    "transfer": { label: "Transfer", family: "handling", path: `<path d="M2.2 8h9.6"/><path d="M9.4 4.8L13.6 8l-4.2 3.2"/>` },
    "holding": { label: "Cold Holding", family: "handling", path: `<path d="M3.2 3.2h9.6v7.6a2.2 2.2 0 0 1-2.2 2.2H5.4a2.2 2.2 0 0 1-2.2-2.2z"/>
     <path d="M8 5.6v2.6l1.9 1.2"/>` },
    "pasteurization": { label: "Pasteurization", family: "thermal", path: `<path d="M1.6 3.6h8.8a2.4 2.4 0 0 1 0 4.8H5.4a2.4 2.4 0 0 0 0 4.8h8.6"/>` },
    "heat-shock": { label: "Heat Shock and Hold", family: "thermal", path: `<path d="M1.5 12.2h3l2.1-8.4 2.6 8.4 1.9-4.2h3.4"/>` },
    "evaporation": { label: "Evaporation", family: "thermal", path: `<path d="M3.4 8.2h9.2v3.6a2 2 0 0 1-2 2H5.4a2 2 0 0 1-2-2z"/>
     <path d="M5.8 6c0-1.6 1.5-1.6 1.5-3.4M9.2 6c0-1.6 1.5-1.6 1.5-3.4"/>` },
    "cooling": { label: "Cooling", family: "thermal", path: `<path d="M4.7 10.4V4.8a1.3 1.3 0 0 1 2.6 0v5.6"/><circle cx="6" cy="11.6" r="2.1"/>
     <path d="M12.4 3.4v7.6"/><path d="M10.2 8.8l2.2 2.4 2.2-2.4"/>` },
    "enzyme-treatment": { label: "Enzyme Treatment", family: "biochem", path: `<path d="M9.8 4.6A4.4 4.4 0 1 0 9.8 11.4L6.6 8z"/><circle cx="12.8" cy="8" r="1.5"/>` },
    "ph-adjustment": { label: "pH Adjustment", family: "biochem", path: `<path d="M8 1.8s3.9 4.4 3.9 6.9a3.9 3.9 0 0 1-7.8 0C4.1 6.2 8 1.8 8 1.8z"/>
     <path d="M6.4 9h3.2M8 7.4v3.2"/>` },
    "membrane-concentration": { label: "Membrane Concentration", family: "separation", path: `<path d="M1.6 6.2h8.4"/><path d="M9.2 4.2L11.8 6.2l-2.6 2"/>
     <path d="M1.4 9.6h13.2" stroke-dasharray="3 2"/>
     <path d="M5.4 7.6v6.8"/><path d="M3.6 12.6L5.4 14.4l1.8-1.8"/>` },
    "diafiltration": { label: "Diafiltration", family: "separation", path: `<path d="M1.6 6.2h8.4"/><path d="M9.2 4.2L11.8 6.2l-2.6 2"/>
     <path d="M1.4 9.6h13.2" stroke-dasharray="3 2"/>
     <path d="M5.4 7.6v6.8"/><path d="M3.6 12.6L5.4 14.4l1.8-1.8"/>
     <path d="M3.2 1.2v3.4"/><path d="M1.5 2.9l1.7 1.7 1.7-1.7"/>` },
    "centrifugation": { label: "Centrifugation", family: "separation", path: `<circle cx="6.2" cy="9.6" r="4.2"/><circle cx="6.2" cy="9.6" r=".85"/>
     <path d="M6.2 5.4V3.4h6.4"/><path d="M10.6 1.8L12.6 3.4l-2 1.6"/>` },
    "decanting": { label: "Decanting", family: "separation", path: `<path d="M2.46 4.99L8.36 2.44 11.24 9.31 5.34 11.86z"/>
     <path d="M11.58 9.57c1.2 1.35 1.9 2.76 2.04 3.75"/>` },
    "depth-filtration": { label: "Depth Filtration", family: "separation", path: `<path d="M2.2 2.8h11.6l-4.3 5.4v5.2l-3 1.4V8.2z"/><path d="M3.8 5.2h8.4"/>` },
    "homogenization": { label: "Homogenization", family: "separation", path: `<g transform="rotate(-12 5.9 8)">
     <path d="M5.9 4.2a3.8 3.8 0 0 0 0 7.6l1.1-1.9-1.1-1.9 1.1-1.9-1.1-1.9z"/></g>
     <g transform="rotate(12 10.1 8)">
     <path d="M10.1 4.2a3.8 3.8 0 0 1 0 7.6l-1.1-1.9 1.1-1.9-1.1-1.9 1.1-1.9z"/></g>` },
    "spray-drying": { label: "Spray Drying", family: "drying", path: `<path d="M2.8 4.8h10.4v3.6L8 14.2 2.8 8.4z"/>
     <path d="M8 1.2v3.6"/><path d="M6.1 7.6L8 4.8l1.9 2.8"/>` },
    "freeze-drying": { label: "Freeze Drying", family: "drying", path: `<path d="M8 1.6v12.8M2.5 4.8l11 6.4M13.5 4.8l-11 6.4"/>` },
    "milling": { label: "Milling", family: "drying", path: `<path d="M2.2 4.2h11.6M2.2 11.8h11.6"/>
     <circle cx="4.9" cy="8" r="1.9"/><circle cx="9.3" cy="8" r="1.2"/>
     <circle cx="12.6" cy="8" r=".75"/>` },
    "blending": { label: "Blending", family: "drying", path: `<path d="M2.2 3.4h3.6c2.6 0 2.6 4.6 5.2 4.6h2.6"/>
     <path d="M2.2 12.6h3.6c2.6 0 2.6-4.6 5.2-4.6"/>
     <path d="M11.4 5.8L13.6 8l-2.2 2.2"/>` },
};
/**
 * A template with no glyph, or one naming a key we have since removed. Rendered
 * as a neutral mark — deliberately plain, so an unchosen glyph reads as a gap
 * to fill rather than as a decision.
 */
export const NEUTRAL_GLYPH = {
    label: "Unspecified",
    family: "handling",
    path: `<circle cx="8" cy="8" r="4.6" stroke-dasharray="1.8 1.6"/>`,
};
export const isDspGlyph = (key) => key != null && Object.prototype.hasOwnProperty.call(DSP_GLYPHS, key);
/** Resolve a template's `icon` to something renderable, always. */
export function glyphFor(key) {
    return isDspGlyph(key) ? DSP_GLYPHS[key] : NEUTRAL_GLYPH;
}
export const glyphColor = (key) => GLYPH_FAMILIES[glyphFor(key).family].color;
export const glyphsByFamily = () => {
    const out = {};
    for (const family of Object.keys(GLYPH_FAMILIES))
        out[family] = [];
    for (const [key, def] of Object.entries(DSP_GLYPHS))
        out[def.family].push(key);
    return out;
};
//# sourceMappingURL=dsp-glyphs.js.map